// Simulación de vuelo de un cohete de una etapa. Ver docs/theory/simulation.md.
import { createAeroModel, dampingSum } from '../aero/model';
import { normalForceCoefficient } from '../aero/barrowman';
import { dragCoefficient } from '../aero/drag';
import { DEFAULT_STALL_ANGLE, ROUGHNESS } from '../aero/flight-conditions';
import { createAtmosphere } from '../atmosphere/isa';
import { constantGravity } from '../gravity/gravity';
import { validateLaunch } from '../launch/launch';
import { createMassModel } from '../mass/mass';
import { type Vec3, ZERO, cross, dot, length, scale, sub, vec3, add } from '../math/vec3';
import { q as Q, type Quat } from '../math';
import { effectiveThrust, toMassProfile, type Motor } from '../motor/motor';
import { mulberry32 } from '../random/prng';
import { referenceDiameter, totalLength } from '../rocket/geometry';
import { listMounts } from '../rocket/mounts';
import { hasErrors, validateRocket } from '../rocket/validate';
import type { Parachute } from '../rocket/types';
import { createWind } from '../wind/wind';
import { rk4Step, type BodyState, type Derivatives } from './integrator';
import { selectTimeStep } from './timestep';
import {
  SimulationInputError,
  type EventType,
  type FlightEvent,
  type FlightPhase,
  type FlightSeries,
  type FlightSummary,
  type SimulationConfig,
  type SimulationProgress,
  type SimulationResult,
  type SimulationStatus,
} from './types';

const DEFAULT_TIME_STEP = 0.05;
const DEFAULT_MAX_ANGLE_STEP = (2 * Math.PI) / 180;
const MAX_ROLL_STEP = (10 * Math.PI) / 180;
const STEPS_PER_PERIOD = 20;
const DEFAULT_MAX_TIME = 1200;
const DEFAULT_PERTURBATION = 1e-3;
const TUMBLE_DRAG_COEFFICIENT = 1.2;
const PARACHUTE_STEP_FACTOR = 0.2;
const NO_LIFTOFF_GRACE = 0.5;
const MAX_ITERATIONS = 5_000_000;
const EPS = 1e-9;

interface Eval {
  readonly acc: Vec3;
  readonly alpha: Vec3;
  readonly thrust: number;
  readonly drag: number;
  readonly mass: number;
  readonly mach: number;
  readonly aoa: number;
  readonly margin: number;
  readonly cgX: number;
  readonly cpX: number;
  readonly cd: number;
  readonly pitchFrequency: number;
  readonly stalled: boolean;
  /** Límite adicional del paso (estabilidad del arrastre en recuperación y volteo). */
  readonly extraLimit?: number;
}

interface Scheduled {
  readonly time: number;
  /** `breakpoint`: vértice de una curva de empuje; solo alinea los pasos y no genera evento. */
  readonly type: 'burnout' | 'ejection-charge' | 'breakpoint';
  readonly detail: string;
}

const NOT_YET = Number.NaN;

export class FlightSimulation {
  private readonly cfg: SimulationConfig;
  private readonly motors: Array<{ motor: Motor; mountId: string; delay: number }>;
  private readonly chutes: Array<{ chute: Parachute; deployed: boolean }> = [];
  private readonly scheduled: Scheduled[] = [];
  private readonly events: FlightEvent[] = [];
  private readonly warnings: string[] = [];
  private readonly warned = new Set<string>();

  private readonly atmosphere;
  private readonly gravity;
  private readonly wind;
  private readonly massModel;
  private readonly aero;
  private readonly rodDir: Vec3;
  private readonly refDiameter: number;
  private readonly rocketLength: number;
  private readonly rng;
  private readonly timeStep: number;
  private readonly maxAngleStep: number;
  private readonly maxTime: number;
  private readonly stall: number;
  private readonly roughness: number;
  private readonly perturbation: number;

  private state: BodyState;
  private phase: FlightPhase = 'pad';
  private lastDt: number | undefined;
  private noiseX = 0;
  private noiseY = 0;
  private lastMargin = Number.NaN;
  private liftedOff = false;
  private apogeeSeen = false;
  private apogee = { time: 0, altitude: 0 };
  private rodVelocity = NOT_YET;
  private landing = { east: 0, north: 0 };
  private landingVelocity = NOT_YET;
  private status: SimulationStatus = 'completed';
  private message: string | undefined;
  private finished = false;
  private iterations = 0;
  private lastBurnout = 0;
  private flightEnd = 0;
  private maxSpeed = 0;
  private maxAcc = 0;
  private maxMach = 0;

  private readonly series: FlightSeries = {
    time: [], altitude: [], east: [], north: [], verticalVelocity: [], speed: [], acceleration: [], mach: [],
    angleOfAttack: [], thrust: [], drag: [], mass: [], cgX: [], cpX: [], dragCoefficient: [], stabilityMargin: [],
  };

  constructor(config: SimulationConfig) {
    this.cfg = config;
    const lookup = config.motors;

    const issues = validateRocket(config.rocket, { motors: (id) => { const m = lookup(id); return m === undefined ? undefined : toMassProfile(m); } });
    const errors = issues.filter((i) => i.severity === 'error').map((i) => `${i.componentId}: ${i.message}`);
    const launchErrors = Object.entries(validateLaunch(config.launch)).map(([k, v]) => `${k}: ${v}`);
    if (hasErrors(issues) || launchErrors.length > 0) throw new SimulationInputError('El diseño o las condiciones de lanzamiento no son válidos', [...errors, ...launchErrors]);
    this.warnings.push(...issues.filter((i) => i.severity === 'warning').map((i) => i.message));

    const launch = config.launch;
    this.atmosphere = config.atmosphere ?? createAtmosphere({ siteAltitude: launch.siteAltitude, siteTemperature: launch.temperature, sitePressure: launch.pressure });
    this.gravity = config.gravity ?? constantGravity();
    this.wind = config.wind ?? createWind({ speed: 0, direction: 0 });
    this.massModel = createMassModel(config.rocket, (id) => { const m = lookup(id); return m === undefined ? undefined : toMassProfile(m); });
    this.aero = createAeroModel(config.rocket);
    this.refDiameter = referenceDiameter(config.rocket);
    this.rocketLength = totalLength(config.rocket);
    this.rng = mulberry32(config.seed ?? 0);
    this.timeStep = config.timeStep ?? DEFAULT_TIME_STEP;
    this.maxAngleStep = config.maxAngleStep ?? DEFAULT_MAX_ANGLE_STEP;
    this.maxTime = config.maxTime ?? DEFAULT_MAX_TIME;
    this.stall = config.stallAngle ?? DEFAULT_STALL_ANGLE;
    this.roughness = config.roughness ?? ROUGHNESS.smooth;
    this.perturbation = config.perturbation ?? DEFAULT_PERTURBATION;

    const sinT = Math.sin(launch.rodAngle);
    this.rodDir = vec3(sinT * Math.sin(launch.rodDirection), sinT * Math.cos(launch.rodDirection), Math.cos(launch.rodAngle));
    const q0: Quat = Q.fromAxisAngle(cross(vec3(0, 0, 1), this.rodDir), launch.rodAngle);
    this.state = { t: 0, pos: ZERO, vel: ZERO, q: q0, omega: ZERO };

    this.motors = [];
    for (const m of listMounts(config.rocket)) {
      if (m.mark.motor === undefined) continue;
      const motor = lookup(m.mark.motor.motorId);
      if (motor !== undefined) this.motors.push({ motor, mountId: m.tube.id, delay: m.mark.motor.ejectionDelay });
    }
    for (const entry of config.rocket.stack) {
      if (entry.type === 'nose-cone') continue;
      for (const child of entry.children) if (child.type === 'parachute') this.chutes.push({ chute: child, deployed: false });
    }

    this.emit('launch', 0);
    for (const m of this.motors) {
      this.emit('ignition', 0, undefined, m.mountId);
      for (const tp of m.motor.time) if (tp > 0 && tp < m.motor.burnTime) this.scheduled.push({ time: tp, type: 'breakpoint', detail: m.mountId });
      this.scheduled.push({ time: m.motor.burnTime, type: 'burnout', detail: m.mountId });
      this.lastBurnout = Math.max(this.lastBurnout, m.motor.burnTime);
      if (!(m.motor.plugged && m.motor.delays.length === 0)) this.scheduled.push({ time: m.motor.burnTime + m.delay, type: 'ejection-charge', detail: m.mountId });
    }
    this.scheduled.sort((a, b) => a.time - b.time);
  }

  // ───────────────────────────── API pública ─────────────────────────────

  get done(): boolean {
    return this.finished;
  }

  get progress(): SimulationProgress {
    return { time: this.state.t, altitude: this.state.pos.z, phase: this.phase };
  }

  /** Avanza hasta `maxSteps` pasos. Devuelve `true` cuando la simulación terminó. */
  advance(maxSteps: number): boolean {
    for (let i = 0; i < maxSteps && !this.finished; i++) this.stepOnce();
    return this.finished;
  }

  /** Interrumpe la simulación conservando los datos calculados. */
  cancel(): void {
    if (this.finished) return;
    this.finish('cancelled', 'Simulación cancelada por el usuario');
  }

  /** Ejecuta hasta terminar y devuelve el resultado. */
  run(): SimulationResult {
    while (!this.finished) this.stepOnce();
    return this.result();
  }

  result(): SimulationResult {
    const data = this.series;
    const sumTime = this.apogeeSeen ? this.apogee.time : data.time[data.altitude.indexOf(Math.max(...data.altitude))] ?? 0;
    const summary: FlightSummary = {
      apogee: this.apogeeSeen ? this.apogee.altitude : Math.max(0, ...data.altitude),
      timeToApogee: sumTime,
      maxVelocity: this.maxSpeed,
      maxAcceleration: this.maxAcc,
      maxMach: this.maxMach,
      flightTime: this.flightEnd,
      landingVelocity: this.landingVelocity,
      rodClearanceVelocity: this.rodVelocity,
      landing: this.landing,
    };
    return { status: this.status, ...(this.message === undefined ? {} : { message: this.message }), events: this.events, data, summary, warnings: this.warnings };
  }

  // ───────────────────────────── bucle de pasos ─────────────────────────────

  private stepOnce(): void {
    if (++this.iterations > MAX_ITERATIONS) return this.finish('error', 'Se superó el número máximo de iteraciones');
    const s = this.state;

    this.processScheduled();
    if (this.finished) return;

    const e = this.evaluate(s);
    this.record(s, e);
    if (e.stalled) this.warnOnce('stall', 'El ángulo de ataque superó el ángulo de pérdida en algún instante del vuelo');

    if (s.t >= this.maxTime) return this.finish('max-time', `Se alcanzó el tiempo máximo de simulación (${this.maxTime} s)`);
    if (!this.liftedOff && s.t >= this.lastBurnout + NO_LIFTOFF_GRACE) {
      this.emit('simulation-end', s.t, undefined, 'El cohete no despegó');
      return this.finish('no-liftoff', 'El cohete no despegó: el empuje no supera el peso');
    }

    const onRod = this.phase === 'pad' || this.phase === 'rod';
    const speed = length(s.vel);
    const nextEvent = this.scheduled.length > 0 ? this.scheduled[0]!.time - s.t : Number.POSITIVE_INFINITY;
    const choice = selectTimeStep({
      userStep: this.timeStep,
      onRod,
      rodLength: this.cfg.launch.rodLength,
      speed,
      timeToEvent: Math.min(nextEvent, this.maxTime - s.t),
      maxAngleStep: this.maxAngleStep,
      lateralRate: Math.hypot(s.omega.x, s.omega.y),
      maxRollStep: MAX_ROLL_STEP,
      rollRate: s.omega.z,
      pitchFrequency: e.pitchFrequency,
      stepsPerPeriod: STEPS_PER_PERIOD,
      ...(this.lastDt === undefined ? {} : { previousStep: this.lastDt }),
      ...(e.extraLimit === undefined ? {} : { extraLimit: e.extraLimit }),
    });
    const dt = choice.dt;
    if (!(dt > 0)) return this.finish('error', 'Paso de tiempo no positivo');

    this.noiseX = (this.rng() * 2 - 1) * this.perturbation;
    this.noiseY = (this.rng() * 2 - 1) * this.perturbation;

    const prev = s;
    let next = rk4Step(s, dt, (st) => this.derivatives(st));
    next = this.constrain(next);
    this.lastDt = dt;

    if (![next.pos, next.vel, next.omega].every((v) => Number.isFinite(v.x + v.y + v.z)) || !Number.isFinite(next.q.w + next.q.x + next.q.y + next.q.z)) {
      return this.finish('error', 'La simulación produjo valores no numéricos');
    }
    this.state = next;
    this.afterStep(prev, next);
  }

  // ───────────────────────────── eventos ─────────────────────────────

  private emit(type: EventType, time: number, velocity?: number, detail?: string): void {
    const altitude = type === 'ground-hit' || this.phase === 'landed' ? 0 : this.state.pos.z;
    this.events.push({ type, time, altitude, ...(velocity === undefined ? {} : { velocity }), ...(detail === undefined ? {} : { detail }) });
  }

  private warnOnce(key: string, message: string): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.warnings.push(message);
  }

  /** Procesa los eventos programados cuyo instante ya llegó. */
  private processScheduled(): void {
    while (this.scheduled.length > 0 && this.scheduled[0]!.time <= this.state.t + EPS) {
      const ev = this.scheduled.shift()!;
      if (ev.type === 'breakpoint') continue;
      this.emit(ev.type, ev.time, length(this.state.vel), ev.detail);
      if (ev.type === 'ejection-charge') this.deploy('ejection');
    }
  }

  private deploy(trigger: 'ejection' | 'apogee' | 'altitude', only?: Parachute): void {
    for (const c of this.chutes) {
      if (c.deployed) continue;
      if (only !== undefined ? c.chute !== only : c.chute.deployment.event !== trigger) continue;
      c.deployed = true;
      this.emit('recovery-device-deployment', this.state.t, length(this.state.vel), c.chute.id);
      if (this.liftedOff && (this.phase === 'flight' || this.phase === 'tumble')) this.phase = 'recovery';
    }
  }

  private afterStep(prev: BodyState, next: BodyState): void {
    const t = next.t;
    const s = dot(next.pos, this.rodDir);

    if (this.phase === 'pad' && s > 1e-6) {
      this.liftedOff = true;
      this.phase = 'rod';
      this.emit('liftoff', t);
    }
    if (this.phase === 'rod' && s >= this.cfg.launch.rodLength) {
      this.phase = 'flight';
      this.rodVelocity = length(next.vel);
      this.emit('launch-rod-clearance', t, this.rodVelocity);
    }

    if (this.phase === 'pad' || this.phase === 'rod') return;

    // Impacto con el suelo.
    if (next.pos.z <= 0 && prev.pos.z > 0) {
      const f = prev.pos.z / (prev.pos.z - next.pos.z);
      const hitT = prev.t + f * (next.t - prev.t);
      const hitVel = add(prev.vel, scale(sub(next.vel, prev.vel), f));
      this.landingVelocity = length(hitVel);
      this.landing = { east: prev.pos.x + f * (next.pos.x - prev.pos.x), north: prev.pos.y + f * (next.pos.y - prev.pos.y) };
      this.flightEnd = hitT;
      this.phase = 'landed';
      this.emit('ground-hit', hitT, this.landingVelocity);
      this.emit('simulation-end', hitT);
      this.record(
        { ...next, t: hitT, pos: vec3(this.landing.east, this.landing.north, 0), vel: hitVel },
        { acc: ZERO, alpha: ZERO, thrust: 0, drag: 0, mass: this.massModel.at(hitT).mass, mach: 0, aoa: Number.NaN, margin: this.lastMargin, cgX: this.massModel.at(hitT).cgX, cpX: Number.NaN, cd: Number.NaN, pitchFrequency: 0, stalled: false },
      );
      this.finished = true;
      return;
    }

    // Apogeo: la velocidad vertical pasa de positiva a no positiva.
    if (!this.apogeeSeen && prev.vel.z > 0 && next.vel.z <= 0) {
      const dt = next.t - prev.t;
      const a = (next.vel.z - prev.vel.z) / dt;
      const tau = a < 0 ? -prev.vel.z / a : dt;
      this.apogee = { time: prev.t + tau, altitude: prev.pos.z + prev.vel.z * tau + 0.5 * a * tau * tau };
      this.apogeeSeen = true;
      this.events.push({ type: 'apogee', time: this.apogee.time, altitude: this.apogee.altitude, velocity: Math.hypot(next.vel.x, next.vel.y) });
      this.deploy('apogee');
      const pending = this.chutes.some((c) => !c.deployed) && (this.scheduled.some((e) => e.type === 'ejection-charge') || this.chutes.some((c) => !c.deployed && c.chute.deployment.event === 'altitude'));
      if (this.phase === 'flight' && !pending) {
        this.phase = 'tumble';
        this.emit('tumble', t, undefined, this.chutes.length === 0 ? 'Sin dispositivo de recuperación' : 'Sin dispositivo de recuperación pendiente');
      }
    }

    // Despliegue por altitud: en descenso al cruzar la altitud indicada.
    if (next.vel.z < 0) {
      for (const c of this.chutes) {
        if (!c.deployed && c.chute.deployment.event === 'altitude' && next.pos.z <= c.chute.deployment.altitude) this.deploy('altitude', c.chute);
      }
    }
  }

  // ───────────────────────────── dinámica ─────────────────────────────

  /** Restricciones de suelo y varilla aplicadas tras cada paso. */
  private constrain(st: BodyState): BodyState {
    if (this.phase !== 'pad' && this.phase !== 'rod') return st;
    const s = dot(st.pos, this.rodDir);
    const vs = dot(st.vel, this.rodDir);
    if (s <= 0 && vs <= 0) return { ...st, pos: ZERO, vel: ZERO, omega: ZERO };
    return { ...st, pos: scale(this.rodDir, s), vel: scale(this.rodDir, vs), omega: ZERO };
  }

  private derivatives(st: BodyState): Derivatives {
    const e = this.evaluate(st);
    return { acc: e.acc, alpha: e.alpha };
  }

  private deployedChuteArea(): number {
    return this.chutes.filter((c) => c.deployed).reduce((sum, c) => sum + c.chute.dragCoefficient * (Math.PI * c.chute.diameter ** 2) / 4, 0);
  }

  private evaluate(st: BodyState): Eval {
    const cfg = this.cfg;
    const altitudeMsl = cfg.launch.siteAltitude + st.pos.z;
    const air = this.atmosphere.at(altitudeMsl);
    const g = this.gravity.at(cfg.launch.latitude, altitudeMsl);
    const vAir = sub(st.vel, this.wind.at(st.t));
    const speed = length(vAir);
    const mass = this.massModel.at(st.t);

    if (this.phase === 'recovery' || this.phase === 'tumble') {
      const cda = this.phase === 'recovery' ? this.deployedChuteArea() : TUMBLE_DRAG_COEFFICIENT * this.refDiameter * this.rocketLength;
      const k = (air.density * cda) / (2 * mass.mass);
      const acc = vec3(-k * speed * vAir.x, -k * speed * vAir.y, -g - k * speed * vAir.z);
      return {
        acc, alpha: ZERO, thrust: 0, drag: k * speed * speed * mass.mass, mass: mass.mass, mach: speed / air.speedOfSound, aoa: Number.NaN,
        margin: this.lastMargin, cgX: mass.cgX, cpX: Number.NaN, cd: Number.NaN, pitchFrequency: 0, stalled: false,
        ...(k * speed > 0 ? { extraLimit: PARACHUTE_STEP_FACTOR / (k * speed) } : {}),
      };
    }

    // Vuelo con el cohete como cuerpo rígido (suelo, varilla y vuelo libre).
    const vb = Q.rotateInverse(st.q, vAir);
    const lateral = Math.hypot(vb.x, vb.y);
    const alpha = Math.atan2(lateral, vb.z);
    const alphaEff = alpha <= Math.PI / 2 ? alpha : Math.PI - alpha;
    const mach = speed / air.speedOfSound;
    const sums = this.aero.sums(mach);
    const cpX = sums.a0 === 0 ? Number.NaN : sums.a1 / sums.a0;
    const dyn = 0.5 * air.density * speed * speed;
    const refArea = this.aero.refArea;

    const { cn, stalled } = normalForceCoefficient(sums.a0, alphaEff, this.stall);
    const fn = cn * dyn * refArea;
    const nx = lateral > 1e-9 ? vb.x / lateral : 0;
    const ny = lateral > 1e-9 ? vb.y / lateral : 0;
    const cd = dragCoefficient(cfg.rocket, { air, airspeed: speed, angleOfAttack: alpha, roughness: this.roughness }).cd;
    const drag = cd * dyn * refArea;
    const axial = vb.z === 0 ? 0 : -Math.sign(vb.z) * drag;

    let thrust = 0;
    for (const m of this.motors) thrust += effectiveThrust(m.motor, st.t, air.pressure);

    const fx = -nx * fn;
    const fy = -ny * fn;
    const fz = thrust + axial;

    const margin = Number.isNaN(cpX) ? Number.NaN : (cpX - mass.cgX) / this.refDiameter;
    this.lastMargin = Number.isNaN(margin) ? this.lastMargin : margin;

    // Momentos respecto al CG (ejes del cuerpo). El CP está a (x_cg − x_cp) a lo largo de +z.
    const rz = Number.isNaN(cpX) ? 0 : mass.cgX - cpX;
    const damping = 0.5 * air.density * speed * refArea * dampingSum(sums, mass.cgX);
    const noiseScale = dyn * refArea * this.refDiameter;
    let mx = -rz * fy - damping * st.omega.x + this.noiseX * noiseScale;
    let my = rz * fx - damping * st.omega.y + this.noiseY * noiseScale;
    if (this.phase === 'pad' || this.phase === 'rod') {
      mx = 0;
      my = 0;
    }
    // Ecuaciones de Euler para un cuerpo axisimétrico: I·ω̇ = M − ω × (I·ω).
    const iyy = mass.iyy;
    const izz = mass.izz;
    const w = st.omega;
    const gx = w.y * izz * w.z - w.z * iyy * w.y;
    const gy = w.z * iyy * w.x - w.x * izz * w.z;
    const alphaVec = this.phase === 'pad' || this.phase === 'rod' ? ZERO : vec3((mx - gx) / iyy, (my - gy) / iyy, 0);

    let acc = Q.rotate(st.q, vec3(fx / mass.mass, fy / mass.mass, fz / mass.mass));
    acc = vec3(acc.x, acc.y, acc.z - g);

    // Suelo y varilla: el movimiento se limita a la dirección de la varilla.
    if (this.phase === 'pad' || this.phase === 'rod') {
      const along = dot(acc, this.rodDir);
      const s = dot(st.pos, this.rodDir);
      const vs = dot(st.vel, this.rodDir);
      acc = s <= 1e-9 && vs <= 1e-9 && along <= 0 ? ZERO : scale(this.rodDir, along);
    }

    const stiffness = Number.isNaN(cpX) ? 0 : dyn * refArea * sums.a0 * (cpX - mass.cgX);
    const pitchFrequency = stiffness > 0 && iyy > 0 ? Math.sqrt(stiffness / iyy) : 0;

    return { acc, alpha: alphaVec, thrust, drag, mass: mass.mass, mach, aoa: alpha, margin, cgX: mass.cgX, cpX, cd, pitchFrequency, stalled };
  }

  // ───────────────────────────── registro y cierre ─────────────────────────────

  private record(s: BodyState, e: Eval): void {
    const d = this.series;
    const sp = length(s.vel);
    d.time.push(s.t);
    d.altitude.push(s.pos.z);
    d.east.push(s.pos.x);
    d.north.push(s.pos.y);
    d.verticalVelocity.push(s.vel.z);
    d.speed.push(sp);
    d.acceleration.push(length(e.acc));
    d.mach.push(e.mach);
    d.angleOfAttack.push(e.aoa);
    d.thrust.push(e.thrust);
    d.drag.push(e.drag);
    d.mass.push(e.mass);
    d.cgX.push(e.cgX);
    d.cpX.push(e.cpX);
    d.dragCoefficient.push(e.cd);
    d.stabilityMargin.push(Number.isNaN(e.margin) ? this.lastMargin : e.margin);
    this.maxSpeed = Math.max(this.maxSpeed, sp);
    this.maxAcc = Math.max(this.maxAcc, length(e.acc));
    this.maxMach = Math.max(this.maxMach, e.mach);
  }

  private finish(status: SimulationStatus, message?: string): void {
    this.status = status;
    if (message !== undefined) this.message = message;
    if (this.flightEnd === 0) this.flightEnd = this.state.t;
    if (status !== 'no-liftoff') this.emit('simulation-end', this.state.t, undefined, message);
    this.finished = true;
  }
}

/** Ejecuta la simulación completa. Lanza `SimulationInputError` si los datos no son válidos. */
export function simulate(config: SimulationConfig): SimulationResult {
  return new FlightSimulation(config).run();
}
